import axios from 'axios';
import { store } from 'store';

const { REACT_APP_BASE_API: baseURL } = process.env;

let _axios: any = axios.create({
  baseURL,
});

// ─── Interceptor de REQUEST ───────────────────────────────────────────────────
// Inyecta el access_token (si existe) como Authorization: Bearer en cada
// petición saliente. Se lee directamente de store.getState() en cada request
// — no se cachea el token en una variable, porque cambiaría tras cada
// refresh y un valor cacheado quedaría obsoleto.
_axios.interceptors.request.use((config: any) => {
  const { access_token } = store.getState().auth || {};

  if (access_token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access_token}`;
  }

  return config;
});

// ─── Refresh automático de sesión ─────────────────────────────────────────────
// Promesa compartida: si varias peticiones reciben 401 casi al mismo tiempo,
// solo la primera dispara /auth/refresh; el resto espera esa misma promesa en
// vez de disparar refreshes en paralelo. Esto es crítico porque el backend
// ROTA el refresh token en cada uso — si dos refreshes concurrentes usaran el
// mismo refresh_token, el segundo llegaría tarde (el token ya fue rotado por
// el primero) y el backend lo interpretaría como reuso de un token robado,
// cerrando TODAS las sesiones del usuario como medida de contención.
//
// Mismo mecanismo ya implementado en la app móvil (src/utils/axios.ts de
// wk-lawyer-registry) — misma lógica, adaptada a que aquí no hay Redux
// Persist ni React Navigation, solo localStorage vía redux-persist y
// react-router.
let refreshPromise: Promise<string> | null = null;

const REFRESH_URL = `${baseURL}auth/refresh`;

const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refresh_token: currentRefreshToken } = store.getState().auth || {};
    if (!currentRefreshToken) {
      throw new Error('No hay refresh token disponible');
    }

    // Llamada con axios "crudo" (no _axios): evita volver a pasar por este
    // mismo interceptor de response si /refresh también respondiera 401
    // (refresh token vencido o revocado), lo que causaría un loop de
    // refresh-de-refresh.
    const { data } = await axios.post(REFRESH_URL, {
      refresh_token: currentRefreshToken,
    });

    store.dispatch({
      type: 'SET_AUTH_TOKENS',
      payload: {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      },
    });

    return data.access_token;
  })();

  try {
    return await refreshPromise;
  } finally {
    // Se libera tanto en éxito como en error para que la próxima petición
    // que falle pueda intentar un refresh nuevo.
    refreshPromise = null;
  }
};

// Logout forzado cuando el refresh mismo falla (refresh token vencido, o
// revocado por detección de reuso). No hay a dónde reintentar: se limpia la
// sesión completa y se manda al usuario a login.
//
// Se usa window.location.hash en vez de history.replace() porque axios.ts
// no es un componente React y por lo tanto no tiene acceso a useHistory().
// HashRouter escucha nativamente los cambios de window.location.hash, así
// que esto navega sin recargar la página — mismo resultado que
// history.replace('/logout') usado en App.tsx, sin necesitar reestructurar
// el router para exponer un singleton de history.
const forceLogout = () => {
  store.dispatch({ type: 'CLEAR_AUTH_TOKENS' });
  store.dispatch({ type: 'SET_USER', payload: null });
  window.location.hash = '/logout';
};

// ─── Extractor de mensajes de error ──────────────────────────────────────────
// Cubre los 4 formatos que produce AllExceptionsFilter + ValidationPipe:
//
// Caso 1 — UnprocessableEntityException('texto') manual:
//   { error: { message: "texto", error: "Unprocessable Entity", statusCode: 422 } }
//   → leer error.message
//
// Caso 2 — ValidationPipe con exceptionFactory:
//   { error: { message: { error: "campo requerido" }, statusCode: 422 } }
//   → leer error.message.error
//
// Caso 3 — Error genérico / 500:
//   { error: "connect ECONNREFUSED" }
//   → leer error directamente (string)
//
// Caso 4 — Guards / 401:
//   { error: { message: "Unauthorized", statusCode: 401 } }
//   → leer error.message
const extractMessage = (data: any): string => {
  const fallback = 'Por favor, revise su conexión';
  if (!data) return fallback;

  const errorField = data.error;

  // Caso 3: error es string directo
  if (typeof errorField === 'string') return errorField;

  if (typeof errorField === 'object' && errorField !== null) {
    const msg = errorField.message;

    // Caso 2: ValidationPipe — message es objeto con .error
    if (typeof msg === 'object' && msg !== null && typeof msg.error === 'string') {
      return msg.error;
    }

    // Caso 1 y 4: message es string directo
    if (typeof msg === 'string') return msg;
  }

  return fallback;
};

// ─── Interceptor de RESPONSE ──────────────────────────────────────────────────
// Ante un 401 (access token vencido) intenta refrescar la sesión una sola vez
// y reintenta la petición original con el token nuevo, ANTES de normalizar el
// error con extractMessage — si el refresh tiene éxito, la petición original
// se resuelve con éxito y nunca llega a considerarse un error para el usuario.
_axios.interceptors.response.use(
  (response: any) => response,
  async (err: any) => {
    const originalRequest = err.config;
    const status = err.response?.status;
    const url = originalRequest?.url ?? '';

    // No intentar refresh sobre el propio endpoint de refresh o de login --
    // un 401 ahí significa que las credenciales/refresh token no sirven,
    // no que el access token venció.
    const isAuthFlowEndpoint = url.includes('auth/refresh') || url.includes('auth/login');

    if (status === 401 && !isAuthFlowEndpoint && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return _axios(originalRequest);
      } catch (refreshError) {
        forceLogout();
        err.friendlyMessage = extractMessage(err.response?.data);
        return Promise.reject(err);
      }
    }

    err.friendlyMessage = extractMessage(err.response?.data);
    return Promise.reject(err);
  },
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const createFormData = (data: any) => {
  var formdata = new FormData();
  for (var key in data) {
    if (Array.isArray(data[key])) {
      for (var _key in data[key]) {
        if (Array.isArray(data[key][_key])) {
          for (var i in data[key][_key]) {
            formdata.append(key + '[' + _key + '][' + i + ']', data[key][_key][i]);
          }
        } else {
          formdata.append(key + '[' + _key + ']', data[key][_key]);
        }
      }
    } else {
      formdata.append(key, data[key]);
    }
  }
  return formdata;
};

_axios.all = axios.all;
_axios.spread = axios.spread;
_axios.upload = (url: string, data: any) => {
  return _axios.post(url, createFormData(data), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export default _axios;
